import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { getTokens, setTokens, clearTokens } from "./token";

const API_BASE_URL = "https://hard-api.netloswash.com";

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

function createApiInstance(config?: AxiosRequestConfig): AxiosInstance {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": "ar",
    },
    ...config,
  });

  instance.interceptors.request.use((request) => {
    // حماية من طلبات الجذر غير المرغوب فيها
    if (request.url === "/" || request.url === "") {
      console.warn("Blocked request to root path:", request.url);
      return Promise.reject(new Error("Root path requests are not allowed"));
    }

    // إضافة token للمصادقة
    const { access: token } = getTokens();
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    return request;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<any>) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
      };

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        getTokens().refresh
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers = {
                  ...originalRequest.headers,
                  Authorization: `Bearer ${token}`,
                };
                resolve(axios(originalRequest));
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { refresh: refreshToken } = getTokens();
          const response = await axios.post(
            `${API_BASE_URL}/auth/jwt/refresh/`,
            {
              refresh: refreshToken,
            }
          );

          const newAccessToken = response.data.access;
          setTokens(newAccessToken, refreshToken || "");

          processQueue(null, newAccessToken);

          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };

          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearTokens();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

export function getApiInstance(config?: AxiosRequestConfig): AxiosInstance {
  return createApiInstance(config);
}

export async function fetcher<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  // حماية من طلبات الجذر
  if (url === "/" || url === "") {
    throw new Error("Root path requests are not allowed");
  }

  const api = createApiInstance();
  const response = await api.get<T>(url, config);
  return response.data;
}
